import PropTypes from "prop-types";
import React from "react";
import { useTranslation } from "react-i18next";
import { Typography, Button, Box } from "@mui/material";

//Custom
import Modal from "../../components/Modal";
import LoadingButton from "../../components/Buttons/LoadingButton";
import CourseTypeIcon from "../../components/Icons/CourseTypeIcon";
import CourseFlags from "../../components/Icons/CourseFlags";
import Table from "../../components/ReactTable/Table";
import ButtonGroup from "../../components/Buttons/ButtonGroup";
import { getDurationString, formatDate } from "../../utils/datetime";

const confirmationModalColumns = t => {
  return [
    {
      Header: t("employees.assignments.changeColumn.title"),
      disableSortBy: true,
      accessor: ({ excluded }) => (
        <Box display="flex" alignItems="center" flexDirection="column">
          <Box>{t("employees.assignments.changeColumn.basic")}</Box>
          {excluded && (
            <Box>{t("employees.assignments.changeColumn.excluded")}</Box>
          )}
        </Box>
      )
    },
    {
      Header: t("pickableTrainingObject.type"),
      disableSortBy: true,
      accessor: ({ courseType }) => (
        <CourseTypeIcon type={courseType} onlineOrIltOnly />
      )
    },
    {
      Header: t("pickableTrainingObject.title"),
      disableSortBy: true,
      accessor: ({ name, flags }) => (
        <Box display="flex" alignItems="center">
          <Box mr={2}>{name}</Box>
          {flags && <CourseFlags flags={flags} />}
        </Box>
      )
    },
    {
      Header: t("pickableTrainingObject.dueDate"),
      disableSortBy: true,
      accessor: ({ dueDate }) => formatDate(dueDate, "MM/DD/YYYY")
    },
    {
      Header: t("pickableTrainingObject.passingScore"),
      disableSortBy: true,
      accessor: "passingScore"
    },
    {
      Header: t("pickableTrainingObject.duration"),
      disableSortBy: true,
      accessor: row =>
        getDurationString(row.durationHours * 60 + row.durationMinutes, t),
      id: "trainingObjects.duration"
    }
  ];
};

/**
 * Shows the admin a list of assignments that will be added for the employee so that they can
 * confirm their changes
 */
const ConfirmationModal = ({
  assignmentsToAdd,
  onCancel,
  onSubmit,
  open,
  employeeName,
  isSubmitting
}) => {
  const { t } = useTranslation();

  return (
    <Modal
      data-qa="add-employee-assignments-confirmation-modal"
      disableEnforceFocus
      showCloseIcon
      maxWidth="lg"
      fillHeight
      fullWidth
      dialogTitle={
        <Typography variant="h4" color="textPrimary">
          {t("employees.assignments.confirmNewAssignmentsTitle")}
        </Typography>
      }
      dialogContent={
        <Box mt={1} display="flex" minHeight={300} flexDirection="column">
          <Typography variant="subtitle1" sx={{ mb: 3 }}>{`${t(
            "employees.assignments.confirmationMessage",
            {
              employeeName
            }
          )}:`}</Typography>
          <Table
            columns={confirmationModalColumns(t)}
            data={assignmentsToAdd}
            maxHeight="unset"
          />
        </Box>
      }
      dialogActions={
        <ButtonGroup align="left" mt={1.5}>
          <Button color="primary" onClick={onCancel}>
            {t("cancelBtn")}
          </Button>
          <LoadingButton
            color="primary"
            variant="contained"
            onClick={onSubmit}
            name={"addAssignments"}
            disabled={isSubmitting}
            pending={isSubmitting}
            data-qa="add-employee-assignments-confirm-button"
          >
            {t("employees.assignments.confirmChangesButton")}
          </LoadingButton>
        </ButtonGroup>
      }
      open={open}
      onClose={onCancel}
    />
  );
};

ConfirmationModal.propTypes = {
  assignmentsToAdd: PropTypes.array,
  onCancel: PropTypes.func,
  onSubmit: PropTypes.func,
  open: PropTypes.bool,
  employeeName: PropTypes.string
};

export default ConfirmationModal;
